import { useState } from "react";
import { useLanguage } from "@/lib/LanguageContext";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquare, MessageCircle, Mail, MapPin, CalendarIcon, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "(781) 475-6126";
const PHONE_LINK = "tel:+17814756126";
const SMS_LINK = "sms:+17814756126";
const WHATSAPP_LINK = "https://wa.me/17814756126";
const EMAIL = "sousaedyy85@hotmail.com";

const serviceKeys = [
  "General Repair", "Engine Diagnostics", "Brake Service", "Oil Change",
  "Transmission", "Suspension & Steering", "Electrical Systems",
  "AC & Heating", "Preventive Maintenance",
];

export const Contact = () => {
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", vehicle_make: "",
    vehicle_model: "", year: "", service_needed: "", message: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error(t.contact.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        preferred_date: date ? date.toLocaleDateString() : "",
        language: lang,
      });
      toast.success(t.contact.success);
      setFormData({ name: "", phone: "", email: "", vehicle_make: "", vehicle_model: "", year: "", service_needed: "", message: "" });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 relative" style={{ background: '#0c2d48' }}>
      <div className="absolute inset-0 noise-overlay" />
      <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left - Contact Info */}
          <div className="lg:col-span-5">
            <span className="text-teal text-xs tracking-[0.4em] uppercase font-body font-semibold mb-4 block">
              {t.contact.subtitle}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl tracking-wide uppercase font-bold text-white mb-6">
              {t.contact.title}
            </h2>
            <div className="w-20 h-[3px] bg-gold mb-10" />

            {/* Contact Methods */}
            <div className="space-y-4">
              <a
                href={PHONE_LINK}
                data-testid="contact-call-btn"
                className="flex items-center gap-4 bg-navy-paper border border-white/5 hover:border-gold/30 p-5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 border border-gold/20">
                  <Phone className="w-5 h-5 text-gold" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-sm tracking-wider uppercase text-white">{t.contact.callUs}</p>
                  <p className="font-body text-sm text-slate-400">{PHONE}</p>
                </div>
              </a>

              <a
                href={SMS_LINK}
                data-testid="contact-sms-btn"
                className="flex items-center gap-4 bg-navy-paper border border-white/5 hover:border-teal/30 p-5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-teal/10 border border-teal/20">
                  <MessageSquare className="w-5 h-5 text-teal" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-sm tracking-wider uppercase text-white">{t.contact.textUs}</p>
                  <p className="font-body text-sm text-slate-400">{PHONE}</p>
                </div>
              </a>

              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="flex items-center gap-4 bg-navy-paper border border-white/5 hover:border-green-500/30 p-5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-500/10 border border-green-500/20">
                  <MessageCircle className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-sm tracking-wider uppercase text-white">{t.contact.whatsapp}</p>
                  <p className="font-body text-sm text-slate-400">{PHONE}</p>
                </div>
              </a>

              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-btn"
                className="flex items-center gap-4 bg-navy-paper border border-white/5 hover:border-white/20 p-5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/5 border border-white/10">
                  <Mail className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-sm tracking-wider uppercase text-white">{t.contact.emailUs}</p>
                  <p className="font-body text-sm text-slate-400">{EMAIL}</p>
                </div>
              </a>

              <a
                href="https://www.google.com/maps/search/?api=1&query=216+Main+St%2C+North+Reading%2C+MA"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-navy-paper border border-white/5 hover:border-white/20 p-5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/5 border border-white/10">
                  <MapPin className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-sm tracking-wider uppercase text-white">{t.contact.visitUs}</p>
                  <p className="font-body text-sm text-slate-400">{t.contact.address}</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right - Form */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} data-testid="lead-capture-form" className="bg-navy-paper border border-white/5 p-8 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Name */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.name} *</Label>
                  <Input
                    data-testid="form-name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none h-12"
                    required
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.phone} *</Label>
                  <Input
                    data-testid="form-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none h-12"
                    required
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.email}</Label>
                  <Input
                    data-testid="form-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none h-12"
                  />
                </div>

                {/* Vehicle Make */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.vehicleMake}</Label>
                  <Input
                    data-testid="form-vehicle-make"
                    value={formData.vehicle_make}
                    onChange={(e) => handleChange("vehicle_make", e.target.value)}
                    className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none h-12"
                  />
                </div>

                {/* Vehicle Model */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.vehicleModel}</Label>
                  <Input
                    data-testid="form-vehicle-model"
                    value={formData.vehicle_model}
                    onChange={(e) => handleChange("vehicle_model", e.target.value)}
                    className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none h-12"
                  />
                </div>

                {/* Year */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.year}</Label>
                  <Input
                    data-testid="form-year"
                    value={formData.year}
                    onChange={(e) => handleChange("year", e.target.value)}
                    className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none h-12"
                    placeholder="2024"
                  />
                </div>

                {/* Service Needed - Select */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.serviceNeeded}</Label>
                  <Select value={formData.service_needed} onValueChange={(val) => handleChange("service_needed", val)}>
                    <SelectTrigger data-testid="form-service-select" className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none h-12">
                      <SelectValue placeholder={t.contact.selectService} />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-dark border-white/10">
                      {serviceKeys.map((s) => (
                        <SelectItem key={s} value={s} className="text-white hover:text-gold">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred Date - Calendar */}
                <div className="space-y-2">
                  <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.preferredDate}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="form-date-picker"
                        className="w-full justify-start text-left bg-navy-dark border-white/10 text-white hover:bg-navy-light/20 hover:text-white rounded-none h-12"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
                        {date ? date.toLocaleDateString() : <span className="text-slate-500">{t.contact.selectDate}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-navy-dark border-white/10" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date()}
                        className="text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2 mt-5">
                <Label className="text-xs tracking-wider uppercase text-slate-400 font-body">{t.contact.message}</Label>
                <Textarea
                  data-testid="form-message"
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  className="bg-navy-dark border-white/10 text-white focus:border-gold focus:ring-1 focus:ring-gold rounded-none min-h-[100px]"
                  rows={4}
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="form-submit-btn"
                disabled={loading}
                className="mt-6 w-full bg-gold text-black hover:bg-gold-hover font-heading text-sm tracking-wider uppercase py-6 rounded-none font-bold transition-all duration-300 hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" strokeWidth={2} />
                    {t.contact.submit}
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
